/**
 * Tutorial Provider Context Component
 * 
 * Provides global tutorial state management using React Context.
 * Handles tutorial navigation, completion tracking, and state persistence.
 */

import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from 'react';
import { TutorialContextType, TutorialState, TutorialStep } from '../types/tutorial';
import { tutorialService, TutorialServiceError, TutorialErrorType } from '../services/TutorialService';
import { prepareTutorialSteps } from '../utils/tutorialHelpers';
import ErrorHandler from '../utils/ErrorHandler';

/**
 * Reload the page while preserving any pending THRIFT link redirect.
 * Skips the reload entirely if the user is on the /link-thrift confirmation
 * page — a reload there would destroy the one-time link token flow.
 */
function safePageReload(): void {
  if (window.location.pathname === '/link-thrift') {
    console.log('[TutorialProvider] Skipping page reload — user is on /link-thrift');
    return;
  }
  const pending = sessionStorage.getItem('pendingLinkRedirect');
  if (pending) {
    localStorage.setItem('pendingLinkRedirect', pending);
  }
  window.location.reload();
}

/**
 * Initial tutorial state with enhanced error tracking
 */
const initialState: TutorialState = {
  isRunning: false,
  stepIndex: 0,
  totalSteps: 0,
  isCompleted: false,
  wasSkipped: false,
  currentSection: null,
  currentSteps: [],
  error: null,
  isLoading: false,
  lastError: null,
  retryCount: 0,
};

/**
 * Tutorial state actions with enhanced error handling
 */
type TutorialAction =
  | { type: 'START_TUTORIAL'; payload: { section: string; steps: TutorialStep[] } }
  | { type: 'STOP_TUTORIAL' }
  | { type: 'RESET_TUTORIAL' }
  | { type: 'COMPLETE_TUTORIAL' }
  | { type: 'SKIP_TUTORIAL' }
  | { type: 'NEXT_STEP' }
  | { type: 'PREVIOUS_STEP' }
  | { type: 'GO_TO_STEP'; payload: { stepIndex: number } }
  | { type: 'SET_ERROR'; payload: { error: string; lastError?: TutorialServiceError } }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_COMPLETION_STATUS'; payload: { isCompleted: boolean } }
  | { type: 'SET_LOADING'; payload: { isLoading: boolean } }
  | { type: 'INCREMENT_RETRY_COUNT' }
  | { type: 'RESET_RETRY_COUNT' };

/**
 * Tutorial state reducer with enhanced error handling
 */
function tutorialReducer(state: TutorialState, action: TutorialAction): TutorialState {
  switch (action.type) {
    case 'START_TUTORIAL':
      return {
        ...state,
        isRunning: true,
        stepIndex: 0,
        totalSteps: action.payload.steps.length,
        currentSection: action.payload.section,
        currentSteps: action.payload.steps,
        isCompleted: false,
        wasSkipped: false,
        error: null,
        lastError: null,
        isLoading: false,
        retryCount: 0,
      };

    case 'STOP_TUTORIAL':
      return {
        ...state,
        isRunning: false,
        stepIndex: 0,
        currentSection: null,
        currentSteps: [],
        error: null,
        lastError: null,
        isLoading: false,
      };

    case 'RESET_TUTORIAL':
      return {
        ...initialState,
        isCompleted: false,
      };

    case 'COMPLETE_TUTORIAL':
      return {
        ...state,
        isRunning: false,
        isCompleted: true,
        wasSkipped: false,
        stepIndex: 0,
        currentSection: null,
        currentSteps: [],
        error: null,
        lastError: null,
        isLoading: false,
        retryCount: 0,
      };

    case 'SKIP_TUTORIAL':
      return {
        ...state,
        isRunning: false,
        wasSkipped: true,
        isCompleted: true, // Skipping counts as completion
        stepIndex: 0,
        currentSection: null,
        currentSteps: [],
        error: null,
        lastError: null,
        isLoading: false,
        retryCount: 0,
      };

    case 'NEXT_STEP':
      if (state.stepIndex < state.totalSteps - 1) {
        return {
          ...state,
          stepIndex: state.stepIndex + 1,
        };
      }
      // If this was the last step, complete the tutorial
      return {
        ...state,
        isRunning: false,
        isCompleted: true,
        wasSkipped: false, // Explicitly set wasSkipped to false for normal completion
        stepIndex: 0,
        currentSection: null,
        currentSteps: [],
        error: null,
        lastError: null,
        isLoading: false,
        retryCount: 0,
      };

    case 'PREVIOUS_STEP':
      return {
        ...state,
        stepIndex: Math.max(0, state.stepIndex - 1),
      };

    case 'GO_TO_STEP':
      return {
        ...state,
        stepIndex: Math.max(0, Math.min(action.payload.stepIndex, state.totalSteps - 1)),
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload.error,
        lastError: action.payload.lastError || null,
        isRunning: false,
        isLoading: false,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
        lastError: null,
        retryCount: 0,
      };

    case 'SET_COMPLETION_STATUS':
      return {
        ...state,
        isCompleted: action.payload.isCompleted,
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload.isLoading,
      };

    case 'INCREMENT_RETRY_COUNT':
      return {
        ...state,
        retryCount: (state.retryCount || 0) + 1,
      };

    case 'RESET_RETRY_COUNT':
      return {
        ...state,
        retryCount: 0,
      };

    default:
      return state;
  }
}

/**
 * Tutorial Context
 */
const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

/**
 * Tutorial Provider Props
 */
interface TutorialProviderProps {
  children: React.ReactNode;
  /** Whether to automatically check tutorial status on mount */
  autoCheckStatus?: boolean;
  /** Whether to filter steps to only show those with available targets */
  filterUnavailableSteps?: boolean;
  /** Whether to show only required steps */
  requiredStepsOnly?: boolean;
}

/**
 * Tutorial Provider Component
 * 
 * Provides tutorial state and actions to child components via React Context.
 * Handles tutorial lifecycle, state persistence, and API communication.
 */
export const TutorialProvider: React.FC<TutorialProviderProps> = ({
  children,
  autoCheckStatus = true,
  filterUnavailableSteps = true,
  requiredStepsOnly = false,
}) => {
  const [state, dispatch] = useReducer(tutorialReducer, initialState);
  const errorHandler = ErrorHandler.getInstance();

  /**
   * Utility function to ensure navbar visibility (mobile fix)
   * Restores navbar display properties that might be affected by tutorial overlay
   */
  const ensureNavbarVisibility = useCallback(() => {
    const navbar = document.querySelector('.navigation') as HTMLElement;
    if (navbar) {
      navbar.style.display = '';
      navbar.style.visibility = 'visible';
      navbar.style.opacity = '1';
      navbar.style.zIndex = '10100';
      navbar.style.position = 'sticky';
      navbar.style.top = '0';
    }
  }, []);

  /**
   * Smart mobile tutorial positioning (calculate best position to avoid overlaps)
   */
  const smartMobileTutorialPositioning = useCallback(() => {
    if (window.innerWidth <= 768) {
      // Check if mobile menu is open
      const mobileMenu = document.querySelector('.nav-links-open');
      const isMobileMenuOpen = mobileMenu !== null;
      
      // Removed mobile-specific adjustments - let react-joyride and CSS handle it
      // This prevents conflicts with positioning systems
      
      // Only ensure tutorial elements are visible
      const tooltips = document.querySelectorAll('.react-joyride__tooltip');
      
      tooltips.forEach((element) => {
        const el = element as HTMLElement;
        // Lower z-index when mobile menu is open, higher when closed
        el.style.zIndex = isMobileMenuOpen ? '9999' : '10300';
        // Only set position for tooltips
        if (Array.from(tooltips).includes(element)) {
          el.style.position = 'fixed';
        }
      });
    }
  }, []);

  /**
   * Handle tutorial service errors with user-friendly messages
   */
  const handleTutorialError = useCallback((error: TutorialServiceError, context: string) => {
    console.error(`Tutorial error in ${context}:`, error);
    
    dispatch({ 
      type: 'SET_ERROR', 
      payload: { 
        error: error.message,
        lastError: error,
      } 
    });

    // Show user-friendly error messages based on error type
    switch (error.type) {
      case TutorialErrorType.NETWORK_ERROR:
        errorHandler.showWarning(
          'Tutorial service unavailable',
          [
            'Check your internet connection',
            'The tutorial will work offline with limited functionality',
            'Try refreshing the page if the problem persists',
          ],
          8000
        );
        break;

      case TutorialErrorType.AUTHENTICATION_ERROR:
        errorHandler.showWarning(
          'Authentication required',
          [
            'Please log in to save tutorial progress',
            'You can still use the tutorial without saving progress',
          ],
          6000
        );
        break;

      case TutorialErrorType.SERVER_ERROR:
        errorHandler.showWarning(
          'Tutorial service temporarily unavailable',
          [
            'The tutorial will work with limited functionality',
            'Your progress may not be saved',
            'Please try again later',
          ],
          8000
        );
        break;

      case TutorialErrorType.TIMEOUT_ERROR:
        errorHandler.showWarning(
          'Tutorial service is slow to respond',
          [
            'Check your internet connection',
            'The tutorial will continue with limited functionality',
          ],
          6000
        );
        break;

      default:
        errorHandler.showWarning(
          'Tutorial system error',
          [
            'The tutorial may not work as expected',
            'Try refreshing the page if you encounter issues',
          ],
          6000
        );
    }
  }, [errorHandler]);

  /**
   * Check if tutorial should be shown for new users with enhanced error handling
   */
  const shouldShowTutorial = useCallback(async (): Promise<boolean> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: { isLoading: true } });
      const isCompleted = await tutorialService.getTutorialStatus();
      dispatch({ type: 'SET_COMPLETION_STATUS', payload: { isCompleted } });
      dispatch({ type: 'RESET_RETRY_COUNT' });
      return !isCompleted;
    } catch (error) {
      // TutorialService now handles authentication errors internally and returns false
      // So we should rarely get exceptions here, but handle them gracefully
      console.warn('Error checking tutorial status, defaulting to show tutorial:', error);
      
      // Default to showing tutorial if we can't check status (safer UX)
      return true;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { isLoading: false } });
    }
  }, []);

  /**
   * Start tutorial for specific section with enhanced error handling
   */
  const startTutorial = useCallback((section?: string) => {
    if (window.location.pathname === '/link-thrift') {
      console.log('[TutorialProvider] startTutorial blocked — user is on /link-thrift');
      return;
    }
    dispatch({ type: 'CLEAR_ERROR' });

    try {
      // Close mobile menu if open (for better mobile tutorial experience)
      const closeMobileMenuEvent = new CustomEvent('closeMobileMenu');
      window.dispatchEvent(closeMobileMenuEvent);

      // Ensure navbar is visible before starting tutorial (mobile fix)
      ensureNavbarVisibility();

      // Force mobile positioning if on mobile device
      setTimeout(smartMobileTutorialPositioning, 100);

      // Determine which section to show
      const targetSection = section || 'main';
      
      // Get prepared tutorial steps for the section
      const steps = prepareTutorialSteps(targetSection as 'main' | 'categories' | 'combined', {
        filterUnavailable: filterUnavailableSteps,
        requiredOnly: requiredStepsOnly,
        validateSteps: true,
      });

      if (!steps || steps.length === 0) {
        const errorMessage = `No tutorial steps available for section: ${targetSection}`;
        dispatch({ 
          type: 'SET_ERROR', 
          payload: { error: errorMessage } 
        });
        
        errorHandler.showWarning(
          'Tutorial not available',
          [
            `No tutorial steps found for the ${targetSection} section`,
            'This might be because the page elements are not ready yet',
            'Try refreshing the page or navigating to a different section',
          ],
          6000
        );
        return;
      }

      console.log(`[TutorialProvider] Starting tutorial for section "${targetSection}" with ${steps.length} steps`);

      dispatch({ 
        type: 'START_TUTORIAL', 
        payload: { section: targetSection, steps } 
      });
    } catch (error) {
      const errorMessage = `Failed to start tutorial: ${error instanceof Error ? error.message : String(error)}`;
      console.error(errorMessage, error);
      
      dispatch({ 
        type: 'SET_ERROR', 
        payload: { error: errorMessage } 
      });
      
      errorHandler.showWarning(
        'Failed to start tutorial',
        [
          'There was an error preparing the tutorial',
          'Try refreshing the page',
          'Contact support if the problem persists',
        ],
        6000
      );
    }
  }, [filterUnavailableSteps, requiredStepsOnly, errorHandler]);

  /**
   * Stop/pause tutorial
   */
  const stopTutorial = useCallback(() => {
    dispatch({ type: 'STOP_TUTORIAL' });
  }, []);

  /**
   * Reset tutorial state with enhanced error handling
   */
  const resetTutorial = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: { isLoading: true } });
      const success = await tutorialService.resetTutorialStatus();
      
      if (success) {
        dispatch({ type: 'RESET_TUTORIAL' });
        errorHandler.showSuccess('Tutorial reset successfully');
      } else {
        throw new Error('Reset operation returned false');
      }
    } catch (error) {
      if (error instanceof TutorialServiceError) {
        handleTutorialError(error, 'resetTutorial');
        
        // For non-critical errors, still reset local state
        if (error.type !== TutorialErrorType.AUTHENTICATION_ERROR) {
          dispatch({ type: 'RESET_TUTORIAL' });
          errorHandler.showWarning(
            'Tutorial reset locally',
            [
              'Tutorial was reset in your browser',
              'Changes may not be saved to your account',
              'Try logging in again to sync your progress',
            ],
            6000
          );
        }
      } else {
        console.error('Failed to reset tutorial status:', error);
        dispatch({ 
          type: 'SET_ERROR', 
          payload: { error: 'Failed to reset tutorial status' } 
        });
        
        errorHandler.showWarning(
          'Reset failed',
          [
            'Failed to reset tutorial status',
            'Try refreshing the page',
            'Contact support if the problem persists',
          ],
          6000
        );
      }
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { isLoading: false } });
    }
  }, [handleTutorialError, errorHandler]);

  /**
   * Mark tutorial as completed with enhanced error handling and retry logic
   * Ensures consistent database and local state updates
   * 
   * IMPORTANT: Only updates backend for 'main' section tutorial.
   * Other sections (categories, etc.) only update local state.
   */
  const completeTutorial = useCallback(async () => {
    // Never run tutorial completion on the THRIFT link page — it triggers
    // page reloads that destroy the one-time link token flow.
    if (window.location.pathname === '/link-thrift') {
      console.log('[TutorialProvider] completeTutorial blocked — user is on /link-thrift');
      dispatch({ type: 'STOP_TUTORIAL' });
      return;
    }
    console.log('[TutorialProvider] completeTutorial called - starting completion process');
    console.log('[TutorialProvider] Current section:', state.currentSection);
    
    // Check if this is the main tutorial that should update backend
    const shouldUpdateBackend = state.currentSection === 'main';
    console.log('[TutorialProvider] Should update backend:', shouldUpdateBackend);
    
    if (!shouldUpdateBackend) {
      // For non-main tutorials (categories, etc.), just update local state
      console.log('[TutorialProvider] Non-main tutorial - updating local state only');
      dispatch({ type: 'COMPLETE_TUTORIAL' });
      errorHandler.showSuccess('Tutorial completed!');
      
      // Ensure navbar is visible after tutorial completion (mobile fix)
      ensureNavbarVisibility();
      
      // No page refresh for non-main tutorials - just close the tutorial
      return;
    }
    
    // Main tutorial - update backend
    const maxRetries = 3;
    let retryCount = 0;

    const attemptCompletion = async (): Promise<boolean> => {
      try {
        dispatch({ type: 'SET_LOADING', payload: { isLoading: true } });
        
        console.log('[TutorialProvider] Main tutorial - calling backend to mark as completed...');
        // First update the database
        const success = await tutorialService.markTutorialCompleted();
        console.log('[TutorialProvider] Backend response:', success);
        
        if (success) {
          // Only update local state if database update succeeded
          dispatch({ type: 'COMPLETE_TUTORIAL' });
          
          console.log('[TutorialProvider] Main tutorial marked as completed successfully in backend and local state');
          errorHandler.showSuccess('Tutorial completed!');
          return true;
        } else {
          console.error('[TutorialProvider] Backend returned false for tutorial completion');
          throw new Error('Database update returned false');
        }
      } catch (error) {
        console.error('[TutorialProvider] Error completing tutorial:', error);
        dispatch({ 
          type: 'SET_ERROR', 
          payload: { error: 'Failed to mark tutorial completed. Please try again.' } 
        });
        return false;
      } finally {
        dispatch({ type: 'SET_LOADING', payload: { isLoading: false } });
      }
    };

    // Retry logic for completion
    while (retryCount < maxRetries) {
      const success = await attemptCompletion();
      
      if (success) {
        dispatch({ type: 'RESET_RETRY_COUNT' });
        
        // Ensure navbar is visible after tutorial completion (mobile fix)
        ensureNavbarVisibility();
        
        // Refresh page to ensure clean state after tutorial completion
        console.log('[TutorialProvider] Scheduling page refresh after main tutorial completion');
        setTimeout(() => {
          console.log('[TutorialProvider] Refreshing page after main tutorial completion');
          safePageReload();
        }, 1000); // Small delay to allow completion message to show
        
        return;
      }
      
      retryCount++;
      dispatch({ type: 'INCREMENT_RETRY_COUNT' });
      
      if (retryCount < maxRetries) {
        console.log(`Retrying tutorial completion (attempt ${retryCount + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, 1000 * retryCount)); // Exponential backoff
      }
    }

    // If all retries failed, still refresh the page to ensure clean state
    console.warn('[TutorialProvider] Tutorial completion failed, but refreshing page anyway');
    errorHandler.showError(
      'Failed to complete tutorial',
      [
        'Unable to save tutorial completion to server',
        'Please check your internet connection and try again',
        'Contact support if the problem persists',
      ],
      10000
    );
    
    // Force refresh even on failure to ensure clean state
    setTimeout(() => {
      console.log('[TutorialProvider] Force refreshing page after completion failure');
      safePageReload();
    }, 2000);
  }, [state.currentSection, handleTutorialError, errorHandler]);

  /**
   * Skip tutorial with enhanced error handling and retry logic
   * Marks tutorial as completed when user dismisses/skips
   * 
   * IMPORTANT: Only updates backend for 'main' section tutorial.
   * Other sections (categories, etc.) only update local state.
   */
  const skipTutorial = useCallback(async () => {
    if (window.location.pathname === '/link-thrift') {
      console.log('[TutorialProvider] skipTutorial blocked — user is on /link-thrift');
      dispatch({ type: 'STOP_TUTORIAL' });
      return;
    }
    console.log('[TutorialProvider] skipTutorial called');
    console.log('[TutorialProvider] Current section:', state.currentSection);
    
    // Check if this is the main tutorial that should update backend
    const shouldUpdateBackend = state.currentSection === 'main';
    console.log('[TutorialProvider] Should update backend:', shouldUpdateBackend);
    
    if (!shouldUpdateBackend) {
      // For non-main tutorials (categories, etc.), just update local state
      console.log('[TutorialProvider] Non-main tutorial - updating local state only');
      dispatch({ type: 'SKIP_TUTORIAL' });
      
      // Persist skip status to localStorage for immediate consistency
      const tutorialState = {
        isCompleted: true,
        wasSkipped: true,
        currentSection: state.currentSection,
        skippedAt: new Date().toISOString(),
      };
      
      try {
        localStorage.setItem('tutorialState', JSON.stringify(tutorialState));
      } catch (storageError) {
        console.warn('Failed to save tutorial state to localStorage:', storageError);
        // Continue without localStorage - not critical
      }
      
      // Ensure navbar is visible after tutorial skip (mobile fix)
      ensureNavbarVisibility();
      
      // No page refresh for non-main tutorials - just close the tutorial
      return;
    }
    
    // Main tutorial - update backend
    const maxRetries = 2; // Fewer retries for skip since it's less critical
    let retryCount = 0;

    const attemptSkip = async (): Promise<boolean> => {
      try {
        dispatch({ type: 'SET_LOADING', payload: { isLoading: true } });
        
        console.log('[TutorialProvider] Main tutorial - calling backend to mark as completed...');
        // First update the database
        const success = await tutorialService.markTutorialCompleted();
        
        if (success) {
          // Only update local state if database update succeeded
          dispatch({ type: 'SKIP_TUTORIAL' });
          
          // Persist skip status to localStorage for immediate consistency
          const tutorialState = {
            isCompleted: true,
            wasSkipped: true,
            currentSection: state.currentSection,
            skippedAt: new Date().toISOString(),
          };
          
          try {
            localStorage.setItem('tutorialState', JSON.stringify(tutorialState));
          } catch (storageError) {
            console.warn('Failed to save tutorial state to localStorage:', storageError);
            // Continue without localStorage - not critical
          }
          
          console.log('[TutorialProvider] Main tutorial skipped and marked as completed successfully');
          return true;
        } else {
          throw new Error('Database update returned false');
        }
      } catch (error) {
        if (error instanceof TutorialServiceError) {
          handleTutorialError(error, 'skipTutorial');
          
          // For authentication errors, skip locally but warn user
          if (error.type === TutorialErrorType.AUTHENTICATION_ERROR) {
            dispatch({ type: 'SKIP_TUTORIAL' });
            errorHandler.showInfo('Tutorial skipped locally (not saved to account)');
            return true;
          }
          
          // For retryable errors, return false to trigger retry
          return !error.retryable;
        } else {
          console.error('Unexpected error skipping tutorial:', error);
          dispatch({ 
            type: 'SET_ERROR', 
            payload: { error: 'Failed to skip tutorial. Please try again.' } 
          });
          return false;
        }
      } finally {
        dispatch({ type: 'SET_LOADING', payload: { isLoading: false } });
      }
    };

    // Retry logic for skip
    while (retryCount < maxRetries) {
      const success = await attemptSkip();
      
      if (success) {
        dispatch({ type: 'RESET_RETRY_COUNT' });
        
        // Ensure navbar is visible after tutorial skip (mobile fix)
        ensureNavbarVisibility();
        
        // Refresh page to ensure clean state after tutorial skip
        setTimeout(() => {
          safePageReload();
        }, 500); // Shorter delay for skip
        
        return;
      }
      
      retryCount++;
      dispatch({ type: 'INCREMENT_RETRY_COUNT' });
      
      if (retryCount < maxRetries) {
        console.log(`Retrying tutorial skip (attempt ${retryCount + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, 500 * retryCount));
      }
    }

    // If all retries failed, still skip locally to avoid blocking user
    console.warn('Failed to save tutorial skip status, skipping locally');
    dispatch({ type: 'SKIP_TUTORIAL' });
    
    // Ensure navbar is visible even when skip fails (mobile fix)
    ensureNavbarVisibility();
    
    // Refresh page to ensure clean state even when skip fails
    setTimeout(() => {
      safePageReload();
    }, 500);
    
    errorHandler.showInfo(
      'Tutorial skipped locally (changes may not be saved)',
      3000
    );
  }, [state.currentSection, handleTutorialError, errorHandler]);

  /**
   * Go to next step
   * If on the last step, complete the tutorial properly (including backend sync)
   */
  const nextStep = useCallback(() => {
    // Check if we're on the last step
    if (state.stepIndex >= state.totalSteps - 1) {
      console.log('[TutorialProvider] nextStep called on last step - triggering completeTutorial');
      // On last step, complete the tutorial (this will call backend)
      completeTutorial();
    } else {
      // Not on last step, just advance
      dispatch({ type: 'NEXT_STEP' });
    }
  }, [state.stepIndex, state.totalSteps, completeTutorial]);

  /**
   * Go to previous step
   */
  const previousStep = useCallback(() => {
    dispatch({ type: 'PREVIOUS_STEP' });
  }, []);

  /**
   * Go to specific step
   */
  const goToStep = useCallback((stepIndex: number) => {
    dispatch({ type: 'GO_TO_STEP', payload: { stepIndex } });
  }, []);

  /**
   * Check tutorial status on mount
   */
  useEffect(() => {
    if (autoCheckStatus) {
      shouldShowTutorial().catch(error => {
        console.error('Failed to check tutorial status on mount:', error);
      });
    }
  }, [autoCheckStatus, shouldShowTutorial]);

  /**
   * Handle tutorial completion when reaching the last step
   * Ensures completion is only triggered once and state is consistent
   * Extended delay to give users time to click the finish button
   */
  useEffect(() => {
    if (state.isRunning && state.stepIndex >= state.totalSteps - 1 && state.totalSteps > 0) {
      // Only auto-complete if we actually have steps and we're on the last one
      // Extended delay to allow user to read the final step and click finish button
      // Most users will click the finish button before this timer expires
      const timer = setTimeout(async () => {
        // Skip auto-complete if the user navigated to the THRIFT link page
        if (window.location.pathname === '/link-thrift') {
          console.log('[TutorialProvider] Skipping auto-complete — user is on /link-thrift');
          return;
        }
        // Double-check we're still running and on the last step to avoid race conditions
        if (state.isRunning && state.stepIndex >= state.totalSteps - 1) {
          console.log('[TutorialProvider] Auto-completing tutorial after extended delay (user did not click finish)');
          await completeTutorial();
        }
      }, 5000); // Increased from 1000ms to 5000ms (5 seconds)

      return () => clearTimeout(timer);
    }
  }, [state.isRunning, state.stepIndex, state.totalSteps, completeTutorial]);

  /**
   * Persist tutorial state to localStorage with enhanced error handling
   */
  useEffect(() => {
    const tutorialState = {
      isCompleted: state.isCompleted,
      wasSkipped: state.wasSkipped,
      currentSection: state.currentSection,
      lastUpdated: new Date().toISOString(),
    };

    try {
      localStorage.setItem('tutorialState', JSON.stringify(tutorialState));
    } catch (error) {
      console.warn('Failed to persist tutorial state to localStorage:', error);
      // Continue without localStorage - not critical for functionality
    }
  }, [state.isCompleted, state.wasSkipped, state.currentSection]);

  /**
   * Restore tutorial state from localStorage on mount
   */
  useEffect(() => {
    try {
      const savedState = localStorage.getItem('tutorialState');
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        if (parsedState.isCompleted) {
          dispatch({ 
            type: 'SET_COMPLETION_STATUS', 
            payload: { isCompleted: parsedState.isCompleted } 
          });
        }
      }
    } catch (error) {
      console.warn('Failed to restore tutorial state:', error);
    }
  }, []);

  /**
   * Continuously enforce mobile tutorial positioning when tutorial is running
   */
  useEffect(() => {
    if (!state.isRunning) return;

    const enforcePositioning = () => {
      smartMobileTutorialPositioning();
    };

    // Initial enforcement
    enforcePositioning();

    // Set up interval to continuously enforce positioning
    const interval = setInterval(enforcePositioning, 1000); // Reduced frequency since TutorialOverlay handles detailed positioning

    // Also enforce on window resize
    window.addEventListener('resize', enforcePositioning);

    // Listen for mobile menu toggle events
    const handleMobileMenuToggle = () => {
      // Small delay to ensure DOM has updated
      setTimeout(enforcePositioning, 50);
    };

    // Listen for clicks on hamburger menu and nav links
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.closest('.nav-hamburger') || target.closest('.nav-link')) {
        handleMobileMenuToggle();
      }
    });

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', enforcePositioning);
    };
  }, [state.isRunning, smartMobileTutorialPositioning]);

  const contextValue: TutorialContextType = useMemo(() => ({
    state,
    startTutorial,
    stopTutorial,
    resetTutorial,
    completeTutorial,
    skipTutorial,
    nextStep,
    previousStep,
    goToStep,
    shouldShowTutorial,
  }), [
    state,
    startTutorial,
    stopTutorial,
    resetTutorial,
    completeTutorial,
    skipTutorial,
    nextStep,
    previousStep,
    goToStep,
    shouldShowTutorial,
  ]);

  return (
    <TutorialContext.Provider value={contextValue}>
      {children}
    </TutorialContext.Provider>
  );
};

/**
 * Hook to use tutorial context
 * 
 * @returns Tutorial context value
 * @throws Error if used outside TutorialProvider
 */
export const useTutorial = (): TutorialContextType => {
  const context = useContext(TutorialContext);
  
  if (context === undefined) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  
  return context;
};

/**
 * Hook to check if tutorial should be shown
 * 
 * Convenience hook that combines tutorial status check with loading state.
 * 
 * @returns Object with shouldShow boolean and loading state
 */
export const useShouldShowTutorial = () => {
  const { shouldShowTutorial, state } = useTutorial();
  const [loading, setLoading] = React.useState(true);
  const [shouldShow, setShouldShow] = React.useState(false);

  React.useEffect(() => {
    const checkStatus = async () => {
      try {
        setLoading(true);
        const show = await shouldShowTutorial();
        setShouldShow(show);
      } catch (error) {
        console.error('Failed to check tutorial status:', error);
        setShouldShow(true); // Default to showing tutorial
      } finally {
        setLoading(false);
      }
    };

    checkStatus();
  }, []); // Remove shouldShowTutorial from dependencies to prevent infinite loop

  return {
    shouldShow: shouldShow && !state.isCompleted,
    loading,
    isCompleted: state.isCompleted,
  };
};